import type { FormEvent } from "react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Dismiss20Regular,
  Save20Regular,
} from "@fluentui/react-icons";
import { PageHeader } from "../../components/PageHeader";
import {
  ApiError,
  getGlossaryTermById,
  updateGlossaryTerm,
  type GlossaryTermPayload,
} from "../../glossario/glossarioApi";
import { APP_LOCALE } from "../../config/locale";

const parseErrorMessage = (error: unknown): string => {
  if (error instanceof ApiError) {
    if (error.status === 401) {
      return "Sessão expirada. Por favor, autentique-se novamente.";
    }
    if (error.status === 403) {
      return "Sem permissões para editar termos.";
    }
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return "Não foi possível atualizar o termo.";
};

const parseLoadError = (error: unknown): string => {
  if (error instanceof ApiError) {
    if (error.status === 401) {
      return "Sessão expirada. Por favor, autentique-se novamente.";
    }
    if (error.status === 403) {
      return "Sem permissões para consultar o glossário.";
    }
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return "Não foi possível carregar o termo.";
};

const formatDate = (value?: string | null): string => {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "—";
  return parsed.toLocaleDateString(APP_LOCALE);
};

export function EditarTermoView() {
  const navigate = useNavigate();
  const { termId } = useParams();
  const [abreviatura, setAbreviatura] = useState("");
  const [designacao, setDesignacao] = useState("");
  const [categoria, setCategoria] = useState("");
  const [sinonimos, setSinonimos] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [lastUpdatedLabel, setLastUpdatedLabel] = useState<string | null>(null);
  const [termSnapshotId, setTermSnapshotId] = useState<number | null>(null);

  useEffect(() => {
    let isMounted = true;

    const loadTerm = async () => {
      if (!termId) {
        setLoadError("Termo inválido.");
        setIsLoading(false);
        return;
      }

      const parsedId = Number(termId);
      if (Number.isNaN(parsedId)) {
        setLoadError("Termo inválido.");
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setLoadError(null);
      setSubmitError(null);

      try {
        const response = await getGlossaryTermById(parsedId);
        if (!isMounted) return;

        setTermSnapshotId(response.id);
        setAbreviatura(response.abbreviation ?? "");
        setDesignacao(response.fullName ?? "");
        setCategoria(response.category ?? "");
        setSinonimos(response.synonyms ?? "");
        setLastUpdatedLabel(formatDate(response.lastModified));
      } catch (error) {
        if (!isMounted) return;
        setLoadError(parseLoadError(error));
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadTerm();

    return () => {
      isMounted = false;
    };
  }, [termId]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitError(null);

    if (!termSnapshotId) {
      setSubmitError("Termo inválido.");
      return;
    }

    if (!abreviatura.trim()) {
      setSubmitError("A abreviatura do termo é obrigatória.");
      return;
    }

    setIsSaving(true);

    const payload: GlossaryTermPayload = {
      abbreviation: abreviatura.trim(),
      fullName: designacao.trim() || null,
      category: categoria.trim() || null,
      synonyms: sinonimos.trim() || null,
    };

    try {
      await updateGlossaryTerm(termSnapshotId, payload);
    } catch (error) {
      setSubmitError(parseErrorMessage(error));
      setIsSaving(false);
      return;
    }

    setIsSaving(false);
    navigate("/glossario", {
      state: { toast: "Termo atualizado com sucesso" },
    });
  };

  const actions = (
    <div className="flex items-center gap-4">
      <button
        className="px-4 py-2 bg-secondary hover:bg-accent border border-border rounded transition-colors flex items-center gap-2 min-h-[44px]"
        style={{ borderRadius: "var(--radius-button)" }}
        onClick={() => navigate("/glossario")}
        disabled={isSaving}
      >
        <Dismiss20Regular className="w-5 h-5" />
        <span className="hidden sm:inline">Cancelar</span>
      </button>
      <button
        className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded transition-colors flex items-center gap-2 min-h-[44px]"
        style={{
          borderRadius: "var(--radius-button)",
          height: "var(--button-height)",
        }}
        onClick={() => {
          const form = document.getElementById(
            "editar-termo-form",
          ) as HTMLFormElement | null;
          form?.requestSubmit();
        }}
        disabled={isSaving || isLoading}
      >
        <Save20Regular className="w-5 h-5" />
        {isSaving ? "A guardar..." : "Guardar Alterações"}
      </button>
    </div>
  );

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Editar Termo"
        subtitle="Atualize os dados do termo."
        actions={actions}
        backButton={{ onClick: () => navigate("/glossario") }}
      />

      <div className="flex-1 overflow-y-auto bg-background">
        <div className="p-4 md:p-6">
          {loadError && (
            <div
              className="bg-card border border-[var(--destructive)] px-4 py-3 mb-6 text-[var(--destructive)]"
              style={{ borderRadius: "var(--radius-card)" }}
            >
              {loadError}
            </div>
          )}

          {submitError && (
            <div
              className="bg-card border border-[var(--destructive)] px-4 py-3 mb-6 text-[var(--destructive)]"
              style={{ borderRadius: "var(--radius-card)" }}
            >
              {submitError}
            </div>
          )}

          <div
            className="text-secondary-text mb-6"
            style={{
              fontSize: "var(--text-caption)",
              fontWeight: "var(--font-weight-normal)",
            }}
          >
            Última atualização: {lastUpdatedLabel ?? "—"}
          </div>

          <form id="editar-termo-form" onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-20">
              <div className="space-y-6">
                <div
                  className="bg-card border border-border p-6 space-y-5"
                  style={{ borderRadius: "var(--radius-card)" }}
                >
                  <h3
                    className="text-foreground"
                    style={{
                      fontSize: "var(--text-section-title)",
                      fontWeight: "var(--font-weight-semi-bold)",
                    }}
                  >
                    Dados do Termo
                  </h3>

                  <div>
                    <label
                      htmlFor="abreviatura"
                      className="block text-foreground mb-2"
                      style={{
                        fontSize: "var(--text-caption)",
                        fontWeight: "var(--font-weight-semi-bold)",
                      }}
                    >
                      Abreviatura{" "}
                      <span className="text-primary">*</span>
                    </label>
                    <input
                      id="abreviatura"
                      type="text"
                      value={abreviatura}
                      onChange={(event) => setAbreviatura(event.target.value)}
                      className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      style={{
                        borderRadius: "var(--radius-button)",
                        fontSize: "var(--text-base)",
                      }}
                      disabled={isLoading}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="designacao"
                      className="block text-foreground mb-2"
                      style={{
                        fontSize: "var(--text-caption)",
                        fontWeight: "var(--font-weight-semi-bold)",
                      }}
                    >
                      Designação Completa
                    </label>
                    <input
                      id="designacao"
                      type="text"
                      value={designacao}
                      onChange={(event) => setDesignacao(event.target.value)}
                      className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      style={{
                        borderRadius: "var(--radius-button)",
                        fontSize: "var(--text-base)",
                      }}
                      disabled={isLoading}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="categoria"
                      className="block text-foreground mb-2"
                      style={{
                        fontSize: "var(--text-caption)",
                        fontWeight: "var(--font-weight-semi-bold)",
                      }}
                    >
                      Categoria
                    </label>
                    <input
                      id="categoria"
                      type="text"
                      value={categoria}
                      onChange={(event) => setCategoria(event.target.value)}
                      className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      style={{
                        borderRadius: "var(--radius-button)",
                        fontSize: "var(--text-base)",
                      }}
                      disabled={isLoading}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="sinonimos"
                      className="block text-foreground mb-2"
                      style={{
                        fontSize: "var(--text-caption)",
                        fontWeight: "var(--font-weight-semi-bold)",
                      }}
                    >
                      Sinónimos
                    </label>
                    <input
                      id="sinonimos"
                      type="text"
                      value={sinonimos}
                      onChange={(event) => setSinonimos(event.target.value)}
                      className="w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      style={{
                        borderRadius: "var(--radius-button)",
                        fontSize: "var(--text-base)",
                      }}
                      disabled={isLoading}
                    />
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
